import dayjs, { Dayjs } from "dayjs";
import { Formik } from "formik";
import React, { useEffect, useMemo } from "react";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";

import {
  $Alert,
  $Button,
  $DateLabel,
  $Divider,
  $Form,
  $Popconfirm,
  $Skeleton,
  $Switch,
  $Tag,
  $Tooltip,
  $Typography,
} from "us.common/components";
import { connect, ConnectedProps } from "react-redux";
import { RootState } from "us.helper/types";
import { nonProductionDays_New } from "us.collection.admin/actions";

type WeekendConfigurationsProps = {
  onClose: () => void;
};

const validationSchema = Yup.object().shape({
  isSaturdayEnabled: Yup.boolean().notRequired(),
  isSundayEnabled: Yup.boolean().notRequired(),
});

/**
 * @description Non Execution Day - Weekend Configurations Component
 * @link Task - https://unicorn-solutions.atlassian.net/browse/UBX4-27274
 * @author Roshan Maddumage <roshanma@unicorn-solutions.com>
 * @since 24/02/2026
 */
const WeekendConfigurations: React.FC<
  WeekendConfigurationsProps & PropsFromRedux
> = ({
  weekendConfigurations,
  saveWeekendConfigurations,
  getWeekendConfigurations,
  onClose,
}) => {
  const { t } = useTranslation();
  const { data, isSubmitting } = weekendConfigurations || {};

  const latestHistoryItem = useMemo(() => {
    try {
      const historyItems: any[] = Array.isArray(data?.history)
        ? data.history
        : [];
      if (historyItems.length === 0) return undefined;
      return historyItems[historyItems.length - 1];
    } catch (error) {
      return undefined;
    }
  }, [data?.history]);

  useEffect(() => {
    getWeekendConfigurations && getWeekendConfigurations({});
  }, []);

  const handleSubmit = (values: any) => {
    saveWeekendConfigurations &&
      saveWeekendConfigurations({
        isSaturdayEnabled: values?.isSaturdayEnabled,
        isSundayEnabled: values?.isSundayEnabled,
      });
  };

  return (
    <Formik
      enableReinitialize
      initialValues={{
        selectedYear: data?.selectedYear ?? dayjs().year(),
        isSaturdayEnabled: data?.isSaturdayEnabled ?? false,
        isSundayEnabled: data?.isSundayEnabled ?? false,
      }}
      onSubmit={handleSubmit}
      validateOnChange
      validateOnBlur
      validationSchema={validationSchema}
    >
      {({ values, handleSubmit, setFieldValue, isValid, dirty }: any) => (
        <>
          <$Form>
            <div>
              <$Alert
                type="info"
                showIcon
                message={t(
                  "US.COLLECTION.ADMIN:NON_PRODUCTION_DAYS.WEEKEND_CONFIG_INFO",
                )}
              />
              <$Skeleton
                loading={weekendConfigurations?.isFetching}
                active
                paragraph={{ rows: 2 }}
                className="mt-4"
              >
                <div className="mt-4">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>{t("US.COMMON:COMMON.SATURDAY")}</div>
                    <$Switch
                      checked={values.isSaturdayEnabled}
                      onChange={(checked: boolean) =>
                        setFieldValue("isSaturdayEnabled", checked)
                      }
                      disabled={
                        dayjs().year() > values?.selectedYear ||
                        (isSubmitting &&
                          values?.selectedYear === dayjs().year())
                      }
                    />
                  </div>
                </div>

                <div className="mt-3">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>{t("US.COMMON:COMMON.SUNDAY")}</div>
                    <$Switch
                      checked={values.isSundayEnabled}
                      onChange={(checked: boolean) =>
                        setFieldValue("isSundayEnabled", checked)
                      }
                      disabled={
                        dayjs().year() > values?.selectedYear ||
                        (isSubmitting &&
                          values?.selectedYear === dayjs().year())
                      }
                    />
                  </div>
                </div>
                <$Divider className="my-4" type="horizontal" />
                <strong>
                  {t(
                    "US.COLLECTION.ADMIN:NON_PRODUCTION_DAYS.PREVIOUS_CONFIGURATIONS",
                  )}
                </strong>
                {latestHistoryItem ? (
                  <div className="mt-3">
                    <div className="d-flex justify-content-around">
                      <div className="py-1 align-items-center">
                        <div>
                          <$Typography.Text type="secondary" className="mr-2">
                            {t("US.COMMON:COMMON.SATURDAY")}
                          </$Typography.Text>
                        </div>
                        <$Tag
                          color={
                            latestHistoryItem?.isSaturdayEnabled
                              ? "green"
                              : "red"
                          }
                        >
                          {latestHistoryItem?.isSaturdayEnabled
                            ? t("COMMON.YES")
                            : t("COMMON.NO")}
                        </$Tag>
                      </div>
                      <div className="py-1 align-items-center">
                        <div>
                          <$Typography.Text type="secondary" className="mr-2">
                            {t("US.COMMON:COMMON.SUNDAY")}
                          </$Typography.Text>
                        </div>
                        <$Tag
                          color={
                            latestHistoryItem?.isSundayEnabled ? "green" : "red"
                          }
                        >
                          {latestHistoryItem?.isSundayEnabled
                            ? t("COMMON.YES")
                            : t("COMMON.NO")}
                        </$Tag>
                      </div>
                      <div className="py-1">
                        <$Typography.Text
                          type="secondary"
                          className="mb-0 mt-0"
                        >
                          {t(
                            "US.COLLECTION.ADMIN:NON_PRODUCTION_DAYS.LAST_MODIFIED_DATE",
                          )}
                        </$Typography.Text>
                        <span className="d-block">
                          {latestHistoryItem?.modifiedDate ? (
                            <$DateLabel
                              value={latestHistoryItem?.modifiedDate}
                            />
                          ) : (
                            "N/A"
                          )}
                        </span>
                      </div>
                      <div className="py-1">
                        <$Typography.Text
                          type="secondary"
                          className="mb-0 mt-0"
                        >
                          {t(
                            "US.COLLECTION.ADMIN:NON_PRODUCTION_DAYS.LAST_MODIFIED_BY",
                          )}
                        </$Typography.Text>
                        <span className="d-block">
                          <$Tooltip
                            placement="topLeft"
                            title={latestHistoryItem?.modifiedBy}
                          >
                            {latestHistoryItem?.modifiedBy || "N/A"}
                          </$Tooltip>
                        </span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="mt-3">
                    <span className="text-muted">
                      {t(
                        "US.COLLECTION.ADMIN:NON_PRODUCTION_DAYS.NO_PREVIOUS_CONFIGURATIONS",
                      )}
                    </span>
                  </div>
                )}
              </$Skeleton>

              <div className="drawer-footer-fixed align-content-center justify-content-end">
                <div>
                  <$Popconfirm
                    title={t("COMMON.SURE_TO_PROCEED_?")}
                    placement="topLeft"
                    data-testid="pop-confirm"
                    okButtonProps={{
                      dataTestid: "pop-confirm-ok",
                    }}
                    onConfirm={() => handleSubmit()}
                    okText={t("COMMON.YES")}
                    cancelText={t("COMMON.NO")}
                  >
                    <$Button
                      type="primary"
                      disabled={!dirty || !isValid || isSubmitting}
                    >
                      {t("US.COMMON:COMMON.SAVE")}
                    </$Button>
                  </$Popconfirm>
                  <$Button onClick={onClose}>
                    {t("US.COMMON:COMMON.CANCEL")}
                  </$Button>
                </div>
              </div>
            </div>
          </$Form>
        </>
      )}
    </Formik>
  );
};

const mapStateToProps = (state: RootState) => {
  const { weekendConfigurations } = state.nonProductionDays;
  return {
    weekendConfigurations,
  };
};

const { weekendConfigurationsUpdate, weekendConfigurations } =
  nonProductionDays_New;

const mapDispatchToProps = {
  saveWeekendConfigurations: weekendConfigurationsUpdate.save,
  getWeekendConfigurations: weekendConfigurations.get,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
export default connector(WeekendConfigurations);
